import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription, combineLatest} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {DepartureUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';

import {
    Departure,
    DepartureOnServerCreated,
    selectDeparturesActionLoading,
    selectDepartureById,
    selectLastCreatedDepartureId,
    ValuationStandard,
    AllValuationStandardRequested,
    selectAllValuationStandard
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';

@Component({
    selector: 'kt-model-edit',
    templateUrl: './departure-edit.component.html',
    styleUrls: ['./departure-edit.component.scss'],
})


export class DepartureEditComponent implements OnInit, OnDestroy {

    model: Departure;
    oldModel: Departure;

    departureForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    valuationStandards: ValuationStandard[] = [];
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    readonly = false

    /**
     * Component constructor
     *
     * @param subheaderService
     * @param activatedRoute
     * @param router
     * @param location
     * @param fb: FormBuilder
     * @param store: Store<AppState>
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private location: Location,
                private fb: UntypedFormBuilder,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectDeparturesActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectDepartureById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initDeparture();
                    }
                });
            } else {
                this.model = new Departure();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initDeparture();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }

        this.store.dispatch(new AllValuationStandardRequested());

        const valuationStandardSubscription = this.store.pipe(
            select(selectAllValuationStandard))
            .subscribe(res => {
                this.valuationStandards = [];
                if (res) {
                    this.valuationStandards = res;
                }
            });

        this.subscriptions.push(valuationStandardSubscription);
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Departures', page: `../default/admin-management/linked-tables/departures`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../default/admin-management/linked-tables/departures/edit/${id}`
                    : `../default/admin-management/linked-tables/departures/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initDeparture() {
        this.createForm();
    }


    createForm() {
        this.departureForm = this.fb.group({
            name: [this.model.name, Validators.required],
            valuation_standard_id: [this.model.valuation_standard_id, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('DEPARTURE.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('DEPARTURE.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.departureForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.departureForm.markAsPristine();
        this.departureForm.markAsUntouched();
        this.departureForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.departureForm.controls;
        /** check form */
        if (this.departureForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateDeparture(this.model);
        } else {
            this.createDeparture(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: DepartureModel
     */
    updateDeparture(_model: Departure) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateDeparture: Update<Departure> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new DepartureUpdated({
            partialdeparture: updateDeparture,
            departure: _model
        }));
        this.oldModel = _model;

        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _model: DepartureModel
     */
    createDeparture(_model: Departure) {
        this.store.dispatch(new DepartureOnServerCreated({departure: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedDepartureId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0 &&
            this.model.valuation_standard_id);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name
                || this.model.valuation_standard_id != this.oldModel.valuation_standard_id;
        }
        return false;
    }

}
